import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";
import { ArrowRight, FileText, FilePlus, Plane } from "lucide-react";

const KPI_ORDER = ["Open", "Under Review", "Quoted", "Closed"];

const Dashboard = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [s, r] = await Promise.all([
                    api.get("/rfq/stats"),
                    api.get("/rfq"),
                ]);
                setStats(s.data);
                setRecent(r.data.slice(0, 6));
            } catch (e) {
                setErr(formatApiError(e));
            }
        })();
    }, []);

    return (
        <div data-testid="dashboard-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                <div>
                    <div
                        className="font-body text-[11px] uppercase tracking-[0.22em] mb-2"
                        style={{ color: "var(--ns-muted)" }}
                    >
                        {isAdmin ? "Administrator overview" : `Welcome, ${user?.name?.split(" ")[0] || ""}`}
                    </div>
                    <h1
                        className="font-display text-3xl lg:text-4xl font-bold tracking-tight"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Dashboard
                    </h1>
                </div>
                {!isAdmin && (
                    <Link
                        to="/portal/rfqs/new"
                        className="ns-btn-primary"
                        data-testid="dashboard-new-rfq"
                    >
                        <FilePlus size={16} /> New RFQ
                    </Link>
                )}
            </div>

            {err && (
                <div
                    className="mb-6 px-4 py-3 border bg-[#fff3f1] border-[#e6c8c2] font-body text-sm"
                    style={{ color: "#7a2218" }}
                >
                    {err}
                </div>
            )}

            {/* KPIs */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--ns-border)] border border-[var(--ns-border)] mb-12">
                {KPI_ORDER.map((status) => (
                    <Kpi
                        key={status}
                        status={status}
                        count={stats?.counts?.[status] ?? "—"}
                    />
                ))}
            </div>

            {/* Recent table */}
            <div className="bg-white border border-[var(--ns-border)]">
                <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--ns-border)]">
                    <h2
                        className="font-display text-base font-semibold uppercase tracking-[0.18em]"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        Recent RFQs
                    </h2>
                    <Link
                        to="/portal/rfqs"
                        className="font-body text-sm font-medium inline-flex items-center gap-1 hover:underline"
                        style={{ color: "var(--ns-ink)" }}
                    >
                        View all <ArrowRight size={14} />
                    </Link>
                </div>

                {recent.length === 0 ? (
                    <div className="px-6 py-16 text-center">
                        <FileText
                            size={32}
                            strokeWidth={1.4}
                            style={{ color: "var(--ns-muted-2)" }}
                            className="mx-auto mb-4"
                        />
                        <p
                            className="font-body text-sm"
                            style={{ color: "var(--ns-muted)" }}
                        >
                            No RFQs yet.
                            {!isAdmin && (
                                <Link
                                    to="/portal/rfqs/new"
                                    className="ml-1 underline"
                                    style={{ color: "var(--ns-ink)" }}
                                >
                                    Create your first request →
                                </Link>
                            )}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr
                                    className="border-b border-[var(--ns-border)]"
                                    style={{ background: "#fafaf8" }}
                                >
                                    <Th>Ticket</Th>
                                    <Th>Aircraft</Th>
                                    {isAdmin && <Th>Client</Th>}
                                    <Th>Date</Th>
                                    <Th>Lines</Th>
                                    <Th>Status</Th>
                                    <Th></Th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent.map((r) => (
                                    <tr
                                        key={r.id}
                                        className="border-b border-[var(--ns-border)] last:border-0 hover:bg-[#fbfaf7]"
                                        data-testid={`dashboard-rfq-row-${r.ticket_number}`}
                                    >
                                        <Td>
                                            <span
                                                className="font-display font-semibold tabular-nums"
                                                style={{ color: "var(--ns-ink)" }}
                                            >
                                                {r.ticket_number}
                                            </span>
                                        </Td>
                                        <Td>
                                            <span className="inline-flex items-center gap-2 font-body text-sm">
                                                <Plane
                                                    size={14}
                                                    style={{ color: "var(--ns-muted)" }}
                                                />
                                                {r.aircraft_type}
                                            </span>
                                        </Td>
                                        {isAdmin && (
                                            <Td>
                                                <div
                                                    className="font-body text-sm"
                                                    style={{ color: "var(--ns-ink)" }}
                                                >
                                                    {r.client_name || "—"}
                                                </div>
                                                {r.client_company && (
                                                    <div
                                                        className="font-body text-xs"
                                                        style={{ color: "var(--ns-muted)" }}
                                                    >
                                                        {r.client_company}
                                                    </div>
                                                )}
                                            </Td>
                                        )}
                                        <Td>
                                            <span
                                                className="font-body text-sm tabular-nums"
                                                style={{ color: "var(--ns-muted)" }}
                                            >
                                                {r.creation_date?.slice(0, 10)}
                                            </span>
                                        </Td>
                                        <Td>
                                            <span
                                                className="font-body text-sm tabular-nums"
                                                style={{ color: "var(--ns-muted)" }}
                                            >
                                                {r.items.length}
                                            </span>
                                        </Td>
                                        <Td>
                                            <StatusBadge status={r.status} size="sm" />
                                        </Td>
                                        <Td>
                                            <Link
                                                to={`/portal/rfqs/${r.id}`}
                                                className="font-body text-sm font-medium inline-flex items-center gap-1 hover:underline"
                                                style={{ color: "var(--ns-ink)" }}
                                            >
                                                Open <ArrowRight size={13} />
                                            </Link>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

const Kpi = ({ status, count }) => {
    const s = {
        Open: "#2e6dd6",
        "Under Review": "#d28321",
        Quoted: "#2e8c4d",
        Closed: "#7a7a7a",
    }[status];
    return (
        <div
            className="bg-white p-7 lg:p-8 flex flex-col gap-3"
            data-testid={`kpi-${status.toLowerCase().replace(/\s+/g, "-")}`}
        >
            <div className="flex items-center gap-2.5">
                <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: s }}
                />
                <span
                    className="font-body text-[11px] font-semibold uppercase tracking-[0.18em]"
                    style={{ color: "var(--ns-muted)" }}
                >
                    {status}
                </span>
            </div>
            <div
                className="font-display text-5xl font-bold tabular-nums"
                style={{ color: "var(--ns-ink)" }}
            >
                {count}
            </div>
            <div
                className="font-body text-xs"
                style={{ color: "var(--ns-muted-2)" }}
            >
                RFQs
            </div>
        </div>
    );
};

const Th = ({ children }) => (
    <th
        className="text-left px-6 py-3.5 font-body text-[11px] font-semibold uppercase tracking-[0.16em]"
        style={{ color: "var(--ns-muted)" }}
    >
        {children}
    </th>
);
const Td = ({ children }) => <td className="px-6 py-4">{children}</td>;

export default Dashboard;
